package er.prateek.crossly.content

import er.prateek.crossly.data.model.ContentManifest
import er.prateek.crossly.data.model.CrosswordPack
import er.prateek.crossly.data.model.PackManifestEntry
import er.prateek.crossly.data.model.Puzzle
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.request.get
import io.ktor.http.HttpStatusCode
import kotlinx.serialization.json.Json

private val remoteJson = Json { ignoreUnknownKeys = true }

/**
 * Fetches content over plain HTTPS GET -- same code on all 5 targets, each
 * platform source set contributing its own ktor engine to the classpath
 * (okhttp/darwin/cio/js) so `HttpClient()` resolves one automatically.
 * Bundled content (via [BundledContentSource]) is always the offline floor:
 * every method here returns null on any failure (network, decode, or
 * checksum mismatch) instead of throwing, so callers can fall back cleanly.
 */
class RemoteContentSource(
    private val baseUrl: String,
    private val httpClient: HttpClient = HttpClient {
        install(HttpTimeout) {
            requestTimeoutMillis = 8_000
            connectTimeoutMillis = 5_000
        }
    },
) {
    suspend fun fetchManifest(): ContentManifest? = runCatching {
        val response = httpClient.get(resolveUrl("manifest.json"))
        if (response.status != HttpStatusCode.OK) return null
        remoteJson.decodeFromString(ContentManifest.serializer(), response.body<String>())
    }.getOrNull()

    /** Downloads the pack at [entry]'s url and rejects it if the SHA-256 doesn't match. */
    suspend fun fetchPack(entry: PackManifestEntry): CrosswordPack? = runCatching {
        val response = httpClient.get(resolveUrl(entry.url))
        if (response.status != HttpStatusCode.OK) return null
        val bytes = response.body<ByteArray>()
        if (sha256Hex(bytes) != entry.checksum) return null
        remoteJson.decodeFromString(CrosswordPack.serializer(), bytes.decodeToString())
    }.getOrNull()

    /** [urlTemplate] contains a literal "{date}" placeholder, replaced with [dateIso] (yyyy-MM-dd, UTC). */
    suspend fun fetchDailyPuzzle(urlTemplate: String, dateIso: String): Puzzle? = runCatching {
        val url = urlTemplate.replace("{date}", dateIso)
        val response = httpClient.get(resolveUrl(url))
        if (response.status != HttpStatusCode.OK) return null
        remoteJson.decodeFromString(Puzzle.serializer(), response.body<String>())
    }.getOrNull()

    private fun resolveUrl(url: String): String =
        if (url.startsWith("http://") || url.startsWith("https://")) url else "$baseUrl/$url"
}
